import { useState } from 'react';
import { NavItem as SanityNavItem } from '@root/sanity/sanity.types';
import IconTriangleDown from '@root/src/components/IconTriangleDown';
import AnimateHeight from '@root/src/components/AnimateHeight';
import { subcategories } from '@root/src/components/Collection/pages/categories';
import TrackedLink from 'components/sanity/TrackedLink';
import { capitalizeFirstLetter } from 'utils/utils';
import { NavTrackingProvider, useNavOpenDetector, useNavTracking } from './analytics';
import HorizontalHoverNav, { HOVER_NAV_CLOSE_DELAY_MS } from './HorizontalHoverNav';
import { navItemLink } from './navEvents';

type NavItemProps = {
  item: SanityNavItem;
  pathname: string;
  setIsVisible: (isVisible: boolean) => void;
  isMobile?: boolean;
} & ClassNameProp;

export const navItemClassNames = `
  relative
  flex
  items-center
  justify-between
  whitespace-nowrap
`;
export const desktopNavItemClassNames = `
  after:absolute
  after:left-0
  after:bottom-0
  after:h-1
  after:w-full
  after:bg-black
  after:transform
  after:scale-x-0
  after:origin-bottom-left
  after:transition-all
  after:duration-200
  after:ease-linear
  hover:after:scale-x-100
  focus:after:scale-x-100
  hover:text-gold-darker
  focus:text-gold-darker
  hover:no-underline
`;

export type NavItemType = 'Main' | 'Sub' | 'Sub Sub';

const urlSegmentsMatch = (a: string, b: string) => {
  if (!a || !b) {
    return false;
  }

  return a === b;
};

export const isActiveNavLink = (pathname: string, href: string, navItemType: NavItemType = 'Main') => {
  const urlPathSegments = pathname.split('/').filter(Boolean);
  const hrefPathSegments = href.split('/').filter(Boolean);

  const [mainUrlPathSegment, subUrlPathSegment, subSubUrlPathSegment] = urlPathSegments;
  const [mainNavItemSegment, subNavItemSegment, subSubNavItemSegment] = hrefPathSegments;

  switch (navItemType) {
    case 'Sub':
      return urlSegmentsMatch(subNavItemSegment ?? '', subUrlPathSegment ?? '');
    case 'Sub Sub':
      return urlSegmentsMatch(subSubNavItemSegment ?? '', subSubUrlPathSegment ?? '');
    default:
      return urlSegmentsMatch(mainNavItemSegment ?? '', mainUrlPathSegment ?? '');
  }
};

export const subSubNavItems = subcategories.map((nav) => {
  return {
    _type: 'link' as const,
    title: nav.slug
      .split('-')
      .map((word) => capitalizeFirstLetter(word))
      .join(' '),
    href: `/collection/tuxedos-and-suits/${nav.slug}`,
    trackerClass: `tracker-nav-subsub_nav-${nav.slug.replace('-', '_')}-250516-160343`,
  };
});

type MobileSubNavItemProps = {
  subNavItem: SanityNavItem;
  pathname: string;
  onNavigate: () => void;
};

const MobileSubNavItem = ({ subNavItem, pathname, onNavigate }: MobileSubNavItemProps) => {
  const [expand, setExpand] = useState<boolean>(false);
  const { trackOpen } = useNavTracking();

  const toggleExpand = () => {
    if (!expand) {
      trackOpen();
    }

    setExpand((prev) => !prev);
  };

  const subSubItems = subNavItem.children?.filter((subSubNavItem) => !subSubNavItem.image?.asset) ?? [];
  const hasDropdown = subSubItems.length > 0;

  const subLinkClassNames = (subHref: string, navItemType: NavItemType, itemTrackerClass?: string) =>
    `text-sm flex md:hover:underline ${
      isActiveNavLink(pathname, subHref, navItemType) ? 'text-gold-darker' : 'text-gray-darker'
    } ${itemTrackerClass ?? ''}`;

  return (
    <div>
      <div className={navItemClassNames}>
        <TrackedLink
          nav
          href={subNavItem.href}
          onClick={onNavigate}
          className={subLinkClassNames(subNavItem.href, 'Sub', subNavItem.trackerClass)}
          {...(subNavItem.newTab && { target: '_blank', rel: 'noopener noreferrer' })}
        >
          {subNavItem.title}
        </TrackedLink>
        {hasDropdown && (
          <button
            type="button"
            aria-label={`Toggle ${subNavItem.title} submenu`}
            aria-expanded={expand}
            onClick={toggleExpand}
            className="pl-8"
          >
            <IconTriangleDown className={expand ? '' : 'rotate-90'} />
          </button>
        )}
      </div>

      {hasDropdown && (
        <AnimateHeight open={expand}>
          <div className={'mt-[10px] ml-12 flex flex-col space-y-[10px]'}>
            {subSubItems.map((subSubNavItem) => (
              <TrackedLink
                key={`${subSubNavItem.title}-${subSubNavItem.href}`}
                nav
                navItem={navItemLink(subSubNavItem, subNavItem.children ?? [])}
                href={subSubNavItem.href}
                onClick={onNavigate}
                className={subLinkClassNames(subSubNavItem.href, 'Sub Sub', subSubNavItem.trackerClass)}
                {...(subSubNavItem.newTab && { target: '_blank', rel: 'noopener noreferrer' })}
              >
                {subSubNavItem.title}
              </TrackedLink>
            ))}
          </div>
        </AnimateHeight>
      )}
    </div>
  );
};

const NavItem = ({ item: { title, href, trackerClass, children }, isMobile, pathname, setIsVisible }: NavItemProps) => {
  const [expand, setExpand] = useState<boolean>(false);
  const { trackOpen } = useNavTracking();
  const dropdown = useNavOpenDetector(HOVER_NAV_CLOSE_DELAY_MS);
  const hasDesktopDropdown = !isMobile && Boolean(children?.length);

  const toggleExpand = () => {
    if (!expand) {
      trackOpen();
    }

    setExpand((prev) => !prev);
  };

  return (
    <div
      className={`group pointer-events-auto py-12 lg:py-0 ${isMobile ? 'border-b-1 border-gray' : ''}`}
      {...(hasDesktopDropdown && { onMouseEnter: dropdown.enter, onMouseLeave: dropdown.leave })}
    >
      {children?.length && isMobile ? (
        <div
          onClick={toggleExpand}
          className={`lg:h-80 ${navItemClassNames} ${
            isActiveNavLink(pathname, href) ? 'text-gold-darker' : 'text-gray-darker'
          } ${trackerClass ?? ''}`}
        >
          <span>{title}</span>
          <IconTriangleDown className={`lg:hidden ${expand ? '' : 'rotate-90'}`} />
        </div>
      ) : (
        <TrackedLink
          nav
          href={href}
          onClick={() => setIsVisible(false)}
          className={`lg:h-80 ${!isMobile ? desktopNavItemClassNames : ''} ${navItemClassNames} ${
            isActiveNavLink(pathname, href) ? 'text-gold-darker after:scale-x-100' : 'text-gray-darker'
          } ${trackerClass ?? ''}`}
        >
          <span>{title}</span>
        </TrackedLink>
      )}

      {isMobile && (
        <AnimateHeight open={expand}>
          <div className={'mt-[10px] mb-2 ml-[10px] flex flex-col space-y-[10px]'}>
            {children
              ?.filter((subNavItem) => !subNavItem.image?.asset)
              .map((subNavItem) => (
                <NavTrackingProvider item={navItemLink(subNavItem, children)} key={`${subNavItem.title}-${subNavItem.href}`}>
                  <MobileSubNavItem subNavItem={subNavItem} pathname={pathname} onNavigate={() => setIsVisible(false)} />
                </NavTrackingProvider>
              ))}
          </div>
        </AnimateHeight>
      )}
      {children?.length ? (
        <HorizontalHoverNav
          navItems={children}
          pathname={pathname}
          {...(hasDesktopDropdown && { onClickCapture: dropdown.click, onClose: dropdown.hide })}
        />
      ) : null}
    </div>
  );
};

export default NavItem;
